import { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Send } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import type { Agent } from "@/types/aiToolkit";
import type { AgentAnalysisResult } from "@/types/communications";
import type { AnalyzeItemInput } from "@/hooks/useCommunicationAnalyze";
import { ensureTelegramSendPermission } from "@/lib/telegramAgentPermissions";
import { useSendTelegramMessage } from "@/hooks/useTelegram";

function extractReplyText(result: AgentAnalysisResult): string {
  const parsed = result.parsed as Record<string, unknown> | undefined;
  if (parsed) {
    if (typeof parsed.reply === "string") return parsed.reply;
    if (typeof parsed.draftBody === "string") return parsed.draftBody;
    if (typeof parsed.body === "string") return parsed.body;
  }
  return result.content;
}

interface AgentTelegramResultsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  agent: Agent | null;
  chatId: string;
  results: AgentAnalysisResult[];
  items: AnalyzeItemInput[];
}

export function AgentTelegramResultsDialog({
  open,
  onOpenChange,
  agent,
  chatId,
  results,
  items,
}: AgentTelegramResultsDialogProps) {
  const { toast } = useToast();
  const sendMutation = useSendTelegramMessage();
  const primaryResult = results.find((r) => !r.error) ?? results[0];
  const initialText = useMemo(
    () => (primaryResult ? extractReplyText(primaryResult) : ""),
    [primaryResult]
  );
  const [replyText, setReplyText] = useState(initialText);

  const handleOpenChange = (next: boolean) => {
    if (next) setReplyText(initialText);
    onOpenChange(next);
  };

  const handleSend = async () => {
    if (!chatId || !replyText.trim()) return;
    try {
      await ensureTelegramSendPermission();
      await sendMutation.mutateAsync({ chatId, text: replyText.trim() });
      toast({ title: "Message sent", description: "Your Telegram reply was delivered." });
      onOpenChange(false);
    } catch (e: unknown) {
      toast({
        title: "Send failed",
        description: e instanceof Error ? e.message : "Could not send message",
        variant: "destructive",
      });
    }
  };

  const itemLabel = items[0]?.label || primaryResult?.itemLabel || "Telegram";

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Send Telegram reply</DialogTitle>
          <DialogDescription>
            {agent?.name || "Agent"} drafted a reply for {itemLabel}. Edit before sending.
          </DialogDescription>
        </DialogHeader>
        <Textarea
          value={replyText}
          onChange={(e) => setReplyText(e.target.value)}
          rows={8}
          className="resize-y"
        />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => void handleSend()}
            disabled={sendMutation.isPending || !replyText.trim() || !chatId}
          >
            {sendMutation.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Send className="h-4 w-4 mr-2" />
            )}
            Send to Telegram
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
